import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

export type UserNameMode = 'admin' | 'public';

/**
 * Renders a user's name in one of two modes:
 *
 * - `public` (default): shows the **display name** (what the user chose
 *   to be addressed as), falling back to first + last when no display
 *   name is set. This is the right choice for user-facing UI.
 *
 * - `admin`: shows the **real first + last name** as the primary label,
 *   followed by the display name in parentheses when it differs (so an
 *   operator can identify the user by their real name even if they
 *   picked a cryptic display name). Falls back to display name when no
 *   first/last is set, and to `fallback` when nothing is available.
 *
 * Inputs accept the raw fields from any DTO; bind whatever your shape
 * calls them (`userName` / `userFullName`, `ownerName` / `ownerFullName`,
 * or `firstName` / `lastName` directly). This keeps the component
 * agnostic of the surrounding model.
 *
 * @example
 *   <app-user-name [displayName]="user.userName" [fullName]="user.userFullName" mode="admin" />
 *   <app-user-name [firstName]="u.firstName" [lastName]="u.lastName" [displayName]="u.displayName" mode="admin" />
 *   <app-user-name [displayName]="user.resolvedDisplayName" mode="public" />
 */
@Component({
  selector: 'app-user-name',
  standalone: true,
  imports: [],
  template: `<ng-container>{{ primary() }}</ng-container>@if (secondary(); as s) {<span class="user-name-secondary"> ({{ s }})</span>}`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .user-name-secondary {
        font-weight: 400;
        color: #6b7280;
        font-size: 0.85em;
        margin-left: 0.15rem;
      }
    `,
  ],
})
export class UserNameComponent {
  /** The display name the user chose, if any. */
  @Input() displayName: string | null | undefined;

  /**
   * Pre-composed "firstName lastName" string. Takes precedence over
   * the individual {@link firstName} / {@link lastName} inputs.
   */
  @Input() fullName: string | null | undefined;

  /** Raw first name; composed with {@link lastName} if `fullName` is not set. */
  @Input() firstName: string | null | undefined;

  /** Raw last name; composed with {@link firstName} if `fullName` is not set. */
  @Input() lastName: string | null | undefined;

  /** `'public'` = display first, `'admin'` = real name first, display in parens. */
  @Input() mode: UserNameMode = 'public';

  /** Shown when no name is available at all. */
  @Input() fallback = '—';

  primary(): string {
    const full = this.resolvedFullName();
    if (this.mode === 'admin') {
      return full || this.displayName || this.fallback;
    }
    return this.displayName || full || this.fallback;
  }

  /**
   * In admin mode, returns the display name to render in parentheses next
   * to the primary label — but only when it would add information (it's
   * set, the primary is the real full name, and they differ).
   */
  secondary(): string | null {
    if (this.mode !== 'admin') return null;
    if (!this.displayName) return null;
    const full = this.resolvedFullName();
    if (!full) return null;
    if (this.displayName === full) return null;
    return this.displayName;
  }

  /** Pre-composed `fullName` wins; otherwise build from first + last. */
  private resolvedFullName(): string | null {
    if (this.fullName) return this.fullName;
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    return this.firstName || this.lastName || null;
  }
}
